import { createContext, useContext, ReactNode, useEffect, useMemo, useState } from 'react'
import { useAuth as useOidcAuth } from 'react-oidc-context'
import api from '../api/axios'

interface SuperAdmin {
  id: string
  email: string
  name: string
  stackId: string // kept for backward-compat with existing pages; holds zitadel user id
  role?: 'owner' | 'admin' | 'viewer' | 'developer'
  can_access_database_tools?: boolean
  can_manage_users?: boolean
  can_modify_settings?: boolean
  permissions?: string[]
}

export interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: SuperAdmin | null
  token: string | null
  getToken: () => Promise<string | null>
  login: () => Promise<void>
  logout: () => Promise<void>
  m365Account: any
}

// Exported so OperatorAuthProvider (Better Auth) can publish into the SAME
// context the existing pages already read. Six pages consume useAuth(); the
// point of bridging rather than rewriting them is that none of them care which
// identity provider produced the value.
export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const oidc = useOidcAuth()
  const [user, setUser] = useState<SuperAdmin | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [lastSub, setLastSub] = useState<string | null>(null)

  const oidcSub = oidc.user?.profile?.sub ?? null
  const oidcEmail = (oidc.user?.profile as any)?.email ?? ''
  const oidcName = (oidc.user?.profile as any)?.name ?? oidcEmail ?? ''

  useEffect(() => {
    let cancelled = false

    const loadUser = async () => {
      if (oidc.isLoading) return
      if (!oidc.isAuthenticated || !oidcSub) {
        if (!cancelled) {
          setUser(null)
          setIsLoading(false)
          setLastSub(null)
        }
        return
      }
      if (oidcSub === lastSub) {
        if (!cancelled && isLoading) setIsLoading(false)
        return
      }

      try {
        const resp = await api.get('/super-admin/auth/verify')
        const apiUser = resp.data?.user || {}

        const merged: SuperAdmin = {
          id: apiUser.id || oidcSub,
          email: apiUser.email || oidcEmail,
          name: apiUser.name || oidcName,
          stackId: apiUser.zitadelUserId || apiUser.stackUserId || oidcSub,
          role: apiUser.role || 'owner',
          can_access_database_tools: apiUser.can_access_database_tools ?? true,
          can_manage_users: apiUser.can_manage_users ?? true,
          can_modify_settings: apiUser.can_modify_settings ?? true,
          permissions: apiUser.permissions || ['manage_users', 'modify_settings', 'access_database_tools'],
        }

        if (!cancelled) {
          setUser(merged)
          setIsLoading(false)
          setLastSub(oidcSub)
        }
      } catch (error) {
        console.error('Failed to verify super admin session', error)
        const fallback: SuperAdmin = {
          id: oidcSub,
          email: oidcEmail,
          name: oidcName,
          stackId: oidcSub,
          role: 'owner',
          can_access_database_tools: true,
          can_manage_users: true,
          can_modify_settings: true,
          permissions: ['manage_users', 'modify_settings', 'access_database_tools'],
        }
        if (!cancelled) {
          setUser(fallback)
          setIsLoading(false)
          setLastSub(oidcSub)
        }
      }
    }

    loadUser()
    return () => {
      cancelled = true
    }
  }, [oidc.isLoading, oidc.isAuthenticated, oidcSub])

  const getToken = async (): Promise<string | null> => {
    return oidc.user?.access_token ?? null
  }

  const login = async () => {
    await oidc.signinRedirect()
  }

  const logout = async () => {
    try {
      await oidc.signoutRedirect()
    } catch (err) {
      console.error('Logout failed:', err)
      await oidc.removeUser()
      window.location.href = '/auth/logout'
    }
    setUser(null)
    setLastSub(null)
  }

  const contextValue = useMemo(() => ({
    isAuthenticated: !!user,
    isLoading,
    user,
    token: oidc.user?.access_token ?? null,
    getToken,
    login,
    logout,
    m365Account: null,
  }), [isLoading, user, oidc.user?.access_token])

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
